import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Heart, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import { useOnboardingTranslations } from "@/hooks/useOnboardingTranslations";
import { useOnboardingPersistence } from "@/hooks/useOnboardingPersistence";
import { SmartInput } from "@/components/onboarding/SmartValidation";
import { BudgetRangeSlider } from "@/components/onboarding/BudgetRangeSlider";
import { DressSizeSelector } from "@/components/onboarding/DressSizeSelector";
import { DressStyleSelector } from "@/components/onboarding/DressStyleSelector";
import { ImageUploader } from "@/components/onboarding/ImageUploader";
import { buildAbsoluteUrl } from "@/lib/basePath";

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  partner_name: string | null;
  wedding_date: string | null;
  notes: string | null;
  organization_id: string;
  dress_size: string | null;
  preferred_styles: string[] | null;
  preferred_brands: string[] | null;
  inspiration_images: string[] | null;
  event_location: string | null;
}

interface Organization {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
}

const TOTAL_STEPS = 4;

export default function QROnboardingEnhanced() {
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const dataLoadedRef = useRef(false);
  const [language, setLanguage] = useState(() => {
    // Initialize language from localStorage or default to en-US
    const savedLanguage = localStorage.getItem('qr-onboarding-language');
    console.log('🌍 QROnboardingEnhanced - Initial language:', savedLanguage || 'en-US');
    return savedLanguage || 'en-US';
  });

  const { t, loading: translationsLoading, translations } = useOnboardingTranslations(language);
  
  // Save language preference when it changes
  useEffect(() => {
    console.log('🌍 QROnboardingEnhanced - Language changed to:', language);
    localStorage.setItem('qr-onboarding-language', language);
  }, [language]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const customerId = searchParams.get('customer');
  const {
    formData,
    updateFormData,
    currentStep,
    setCurrentStep,
    hasRestoredData,
    clearPersistedData,
  } = useOnboardingPersistence(customerId || undefined);

  // Fetch customer data and organization branding
  useEffect(() => {
    if (dataLoadedRef.current) return; // Prevent multiple loads
    
    const fetchData = async () => {
      console.log('Enhanced QR Onboarding loaded');
      
      const customerParam = searchParams.get('customer');
      const nameParam = searchParams.get('name');
      
      if (customerParam) {
        try {
          const { data: result, error } = await supabase.functions.invoke('customer-onboarding-data', {
            body: { customerId: customerParam }
          });
          
          if (error) {
            console.error('Error fetching data:', error);
            toast({
              title: "Error",
              description: "Could not load your information",
              variant: "destructive"
            });
          } else if (result?.customer) {
            const data = result.customer;
            const orgData = result.organization;
            
            setCustomer(data);
            setOrganization(orgData);
            
            // Only update with database data, don't overwrite user input
            updateFormData({
              firstName: data.first_name || nameParam || "",
              lastName: data.last_name || "",
              email: data.email || "",
              phone: data.phone || "",
              partnerName: data.partner_name || "",
              weddingDate: data.wedding_date || "",
              notes: data.notes || "",
              eventLocation: data.event_location || "",
              dressSize: data.dress_size || "",
              preferredStyles: data.preferred_styles || [],
              preferredBrands: data.preferred_brands || [],
              inspirationImages: data.inspiration_images || [],
            });
            
            dataLoadedRef.current = true;
          }
        } catch (error) {
          console.error('Exception fetching data:', error);
        }
      }
      
      setPageLoading(false);
    };

    fetchData();
  }, [searchParams]); // Only depend on searchParams

  // Show loading state
  if (pageLoading || translationsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-elegant">
        <div className="text-center space-y-4">
          <div className="animate-pulse text-lg">{t('loading', 'Loading...')}</div>
          <div className="text-sm text-muted-foreground">Preparing your personalized form...</div>
        </div>
      </div>
    );
  }

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const sendMagicLink = async (email: string) => {
    const redirectUrl = buildAbsoluteUrl('/');
    
    const { error } = await supabase.functions.invoke('generate-magic-link', {
      body: {
        email,
        redirectUrl,
        expiryMinutes: 30
      }
    });

    if (error) {
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    
    try {
      if (!customerId) {
        throw new Error('Customer ID not found');
      }

      // Update customer with all the collected data
      const { data: result, error: updateError } = await supabase.functions.invoke('customer-onboarding-update', {
        body: {
          customerId,
          updateData: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            partner_name: formData.partnerName,
            wedding_date: formData.weddingDate || null,
            notes: formData.notes,
            dress_size: formData.dressSize,
            preferred_styles: formData.preferredStyles,
            preferred_brands: formData.preferredBrands,
            inspiration_images: formData.inspirationImages,
            event_location: formData.eventLocation,
          }
        }
      });
        
      if (updateError || !result?.customer) {
        throw updateError || new Error('Failed to update profile');
      }

      // Send magic link
      await sendMagicLink(formData.email);
      
      // Clear persisted data on successful completion
      clearPersistedData();
      
      toast({
        title: "Welcome!",
        description: t('success_message', 'Profile completed! Check your email for a secure sign-in link.'),
      });

      // Store completion flag
      localStorage.setItem('customer_onboarding_complete', customerId);
      
      // Redirect to portal
      navigate('/');
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.email;
      case 2:
        return true; // Optional fields
      case 3:
        return true; // Preferences are optional
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return t('step1_title', 'Contact Information');
      case 2:
        return t('step2_title', 'Event Details');
      case 3:
        return t('step3_title', 'Dress Preferences');
      case 4:
        return t('step4_title', 'Step 4 of 4: Review & Complete');
      default:
        return '';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SmartInput
                id="firstName"
                label={t('first_name', 'First Name')}
                value={formData.firstName}
                onChange={(value) => updateFormData({ firstName: value })}
                required
                placeholder={t('first_name', 'First Name')}
              />
              
              <SmartInput
                id="lastName"
                label={t('last_name', 'Last Name')}
                value={formData.lastName}
                onChange={(value) => updateFormData({ lastName: value })}
                required
                placeholder={t('last_name', 'Last Name')}
              />
            </div>
            
            <SmartInput
              id="email"
              label={t('email', 'Email')}
              type="email"
              value={formData.email}
              onChange={(value) => updateFormData({ email: value })}
              required
              placeholder={t('email', 'Email')}
            />
            
            <SmartInput
              id="phone"
              label={t('phone', 'Phone')}
              type="tel"
              value={formData.phone}
              onChange={(value) => updateFormData({ phone: value })}
              placeholder={t('phone', 'Phone')}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <SmartInput
              id="partnerName"
              label={t('partner_name', 'Partner Name')}
              value={formData.partnerName}
              onChange={(value) => updateFormData({ partnerName: value })}
              placeholder={t('partner_name', 'Partner Name')}
            />
            
            <SmartInput
              id="weddingDate"
              label={t('wedding_date', 'Wedding Date')}
              type="date"
              value={formData.weddingDate}
              onChange={(value) => updateFormData({ weddingDate: value })}
            />
            
            <SmartInput
              id="eventLocation"
              label={t('event_location', 'Event Location')}
              value={formData.eventLocation}
              onChange={(value) => updateFormData({ eventLocation: value })}
              placeholder={t('event_location', 'Event Location')}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <BudgetRangeSlider
              min={formData.budgetMin}
              max={formData.budgetMax}
              onChange={(min, max) => updateFormData({ budgetMin: min, budgetMax: max })}
              currency="$"
              t={t}
            />
            
            <ImageUploader
              images={formData.inspirationImages}
              onChange={(images) => updateFormData({ inspirationImages: images })}
              maxImages={5}
              t={t}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <h4 className="font-medium text-lg">{t('profile_summary', 'Profile Summary')}</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>{t('name_label', 'Name')}:</strong> {formData.firstName} {formData.lastName}</p>
                  <p><strong>Email:</strong> {formData.email}</p>
                  {formData.phone && <p><strong>{t('phone_label', 'Phone')}:</strong> {formData.phone}</p>}
                  {formData.partnerName && <p><strong>{t('partner_label', 'Partner')}:</strong> {formData.partnerName}</p>}
                </div>
                
                <div>
                  {formData.weddingDate && (
                    <p><strong>{t('wedding_date_label', 'Wedding Date')}:</strong> {new Date(formData.weddingDate).toLocaleDateString()}</p>
                  )}
                  {formData.eventLocation && <p><strong>Location:</strong> {formData.eventLocation}</p>}
                  {formData.dressSize && <p><strong>Dress Size:</strong> {formData.dressSize}</p>}
                  <p><strong>{t('budget_label', 'Budget')}:</strong> €{formData.budgetMin.toLocaleString()} - €{formData.budgetMax.toLocaleString()}</p>
                </div>
              </div>
              
              {formData.preferredStyles.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Preferred Styles:</p>
                  <div className="flex flex-wrap gap-2">
                    {formData.preferredStyles.map((style) => (
                      <Badge key={style} variant="secondary">{style}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {formData.inspirationImages.length > 0 && (
                <div>
                  <p className="font-medium mb-2">{t('inspiration_images', 'Inspiration Images')}: {formData.inspirationImages.length}</p>
                </div>
              )}
            </div>
            
            <div className="text-center text-sm text-muted-foreground">
              <p>{t('success_message', 'We\'ll send you a secure sign-in link to access your customer portal.')}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: organization?.primary_color 
          ? `linear-gradient(135deg, ${organization.primary_color}10, ${organization.secondary_color || organization.primary_color}05)`
          : 'hsl(var(--gradient-elegant))'
      }}
    >
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-center">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1" />
            
            {/* Logo or Icon */}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
              {organization?.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={organization.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <Heart className="h-6 w-6" />
              )}
            </div>
            
            <div className="flex-1 flex justify-end">
              <LanguageSelector 
                value={language} 
                onChange={setLanguage}
                className="scale-90"
              />
            </div>
          </div>
          
          {/* Restore Progress Alert */}
          {hasRestoredData && (
            <div className="mb-4 p-3 bg-info/10 border border-info/20 rounded-lg">
              <p className="text-sm text-info-foreground">
                <CheckCircle className="inline h-4 w-4 mr-2" />
                {t('welcome_back_message', 'Welcome back! We\'ve restored your progress.')}
              </p>
            </div>
          )}
          
          <CardTitle className="text-2xl">
            {organization ? `Welcome to ${organization.name}` : t('title', 'Complete Your Profile')}
          </CardTitle>
          <CardDescription>
            {t('subtitle', 'Help us personalize your experience by providing some information.')}
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex justify-center mt-6">
            <div className="flex space-x-2">
              {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((i) => (
                <div key={i} className="flex items-center">
                  <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      i < currentStep 
                        ? 'bg-primary text-primary-foreground' 
                        : i === currentStep
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary/20'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {i < currentStep ? <CheckCircle className="h-4 w-4" /> : i}
                  </div>
                  {i < TOTAL_STEPS && (
                    <div 
                      className={`h-0.5 w-8 mx-2 ${
                        i < currentStep ? 'bg-primary' : 'bg-muted'
                      }`} 
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-3">
            <p className="text-sm text-muted-foreground">
              Step {currentStep} of {TOTAL_STEPS}: {getStepTitle()}
            </p>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-8">
          {/* Step Content */}
          <div className="min-h-[400px]">
            {renderStep()}
          </div>


          {/* Navigation */}
          <div className="flex justify-between pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="min-w-[100px]"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back_button', 'Back')}
            </Button>
            
            <div className="text-center text-xs text-muted-foreground">
              {t('auto_save_message', 'Your progress is automatically saved')}
            </div>
            
            <div>
              {currentStep < TOTAL_STEPS ? (
                <Button 
                  onClick={handleNext} 
                  disabled={!isStepValid()}
                  className="min-w-[100px]"
                >
                  {t('next_button', 'Next')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  disabled={isLoading || !isStepValid()}
                  className="min-w-[100px]"
                >
                  {isLoading ? t('loading', 'Loading...') : t('complete_button', 'Complete')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}